<template>
  <section :class="['notifications-panel', { open }]">
    <align class="header" flex>
      <h5 class="secondary-font">
        Notifications
      </h5>

      <align small-gap>
        <dropdown
          v-if="!showEmptyState"
          align="right"
          :active="menuOpen"
          @close="menuOpen = false"
        >
          <icon-button
            icon="more_vert"
            @click="menuOpen = !menuOpen"
          />

          <template #menu>
            <dropdown-menu
              :options="menuOptions"
              @select="handleSelect"
            />
          </template>
        </dropdown>

        <icon-button
          icon="close"
          @click="$store.commit('SET_NOTIFICATIONS_OPEN', false)"
        />
      </align>
    </align>

    <empty-state
      v-if="showEmptyState"
      icon="notifications_off"
      description="You have no notifications at the moment"
    />

    <template v-else-if="loaded">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="item"
      >
        <notification-item
          :notification="notification"
          @read="markNotficationAsRead"
        />
      </div>
    </template>

    <spinner v-else />
  </section>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import { notificationService } from '@/services';

import NotificationItem from './NotificationItem';

export default {
  components: {
    NotificationItem,
  },

  data() {
    return {
      notifications: [],
      loading: false,
      loaded: false,
      menuOpen: false,
      menuOptions: [
        { value: 'read', label: 'Mark all as read' },
        { value: 'delete', label: 'Delete all' },
        { value: 'settings', label: 'Settings' },
      ],
    };
  },

  watch: {
    open(value) {
      if (value) {
        this.load();
      }
    },
  },

  computed: {
    ...mapState({
      open: (state) => state.notificationsOpen,
      user: (state) => state.user,
    }),

    showEmptyState() {
      return !this.loading && this.loaded && this.notifications.length === 0;
    },
  },

  methods: {
    async load() {
      this.loading = true;

      try {
        this.notifications = await notificationService.getNotifications(this.user.uid, 50);
      } catch (e) {
        this.$toasted.error('Error loading your notifications');
        Sentry.captureException(e);
      }

      this.loading = false;
      this.loaded = true;
    },

    markNotficationAsRead(id) {
      this.$set(this.notifications[this.notifications.findIndex(({ id: notificationId }) => notificationId === id)], 'read', true);
    },

    handleSelect(value) {
      this.menuOpen = false;

      if (value === 'read') {
        this.markAllAsRead();
      } else if (value === 'delete') {
        this.deleteAllNotifications();
      } else if (value === 'settings') {
        this.$store.commit('SET_NOTIFICATIONS_OPEN', false);
        this.$router.push('/account');
      }
    },

    async deleteAllNotifications() {
      if (window.confirm('Are you sure you want to delete all notifications?')) {
        try {
          await notificationService.deleteAllNotifications(this.user.uid);
          this.notifications = [];
          this.$toasted.success('Deleted all notifications!');
          this.$store.commit('SET_NOTIFICATIONS_OPEN', false);
        } catch (e) {
          this.$toasted.error('Error deleting your notifications');
          Sentry.captureException(e);
        }
      }
    },

    async markAllAsRead() {
      try {
        await notificationService.markAllNotificationsAsRead(this.user.uid);
        this.notifications.forEach(({ id }) => {
          this.markNotficationAsRead(id);
        });

        this.$toasted.success('Marked all as read!');
      } catch (e) {
        this.$toasted.error('Error marking the notifications');
        Sentry.captureException(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications-panel {
  @include overflowY;
  @include transition(transform);

  background-color: var(--theme-background);
  box-shadow: $elevation-1;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  position: fixed;
  z-index: 1;
  display: flex;
  flex-direction: column;
  top: 0;
  right: 0;
  transform: translateX(100%);
  bottom: 0;
  z-index: 10;
  border-left: solid 1px var(--theme-border);

  &.open {
    transform: translateX(0);
  }
}

.header {
  min-height: var(--header-height);
  border-bottom: 1px solid var(--theme-border);
  padding: 0 $gp;

  h5 {
    margin-bottom: 0;
  }
}
</style>
